import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { registerApplication, start, navigateToUrl } from 'single-spa';
import { constructApplications, constructLayoutEngine, constructRoutes } from 'single-spa-layout';

// Expose single-spa navigation globally so MFEs can navigate consistently
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).singleSpa = {
  // Preserve any existing properties
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...(window as any).singleSpa,
  navigateToUrl
};

// Convenience helper for a universal "Back to Dashboard" action
// Use hard navigation to avoid inter-app redirect loops/history artifacts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).navigateToDashboard = (): void => {
  try {
    // Prefer hard replace to avoid bouncing back to previous MFE routes
    window.location.replace('/dashboard');
  } catch {
    // Fallback to single-spa navigation if replace fails for any reason
    navigateToUrl('/dashboard');
  }
};

// Create a global back button that is independent of individual MFEs
function ensureGlobalBackButton(): void {
  const existing = document.getElementById('global-back-to-dashboard');
  if (existing) {
    return;
  }
  const btn = document.createElement('button');
  btn.id = 'global-back-to-dashboard';
  btn.type = 'button';
  btn.innerHTML =
    '<span style="display:inline-flex;align-items:center;gap:8px"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 6L9 12L15 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
  btn.style.position = 'fixed';
  btn.style.top = '16px';
  btn.style.left = '16px';
  btn.style.zIndex = '2147483647';
  btn.style.padding = '10px 14px';
  btn.style.borderRadius = '9999px';
  btn.style.border = '1px solid rgba(17,24,39,0.12)';
  btn.style.background = 'rgba(255,255,255,0.9)';
  btn.style.backdropFilter = 'saturate(180%) blur(8px)';
  btn.style.color = '#111827';
  btn.style.boxShadow = '0 8px 20px rgba(0,0,0,0.12)';
  btn.style.cursor = 'pointer';
  btn.style.fontSize = '14px';
  btn.style.lineHeight = '18px';
  btn.style.fontWeight = '600';
  btn.style.letterSpacing = '0.01em';
  btn.style.display = 'inline-flex';
  btn.style.alignItems = 'center';
  btn.style.gap = '8px';
  btn.style.userSelect = 'none';
  btn.style.transition = 'transform 120ms ease, box-shadow 120ms ease, background 120ms ease';
  btn.style.outline = 'none';
  btn.onmouseenter = () => {
    btn.style.transform = 'translateY(-1px)';
    btn.style.boxShadow = '0 12px 28px rgba(0,0,0,0.18)';
    btn.style.background = 'rgba(255,255,255,0.98)';
  };
  btn.onmouseleave = () => {
    btn.style.transform = 'none';
    btn.style.boxShadow = '0 8px 20px rgba(0,0,0,0.12)';
    btn.style.background = 'rgba(255,255,255,0.9)';
  };
  btn.onfocus = () => {
    btn.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.35), 0 12px 28px rgba(0,0,0,0.18)';
  };
  btn.onblur = () => {
    btn.style.boxShadow = '0 8px 20px rgba(0,0,0,0.12)';
  };
  btn.onclick = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).navigateToDashboard();
  };

  document.body.appendChild(btn);

  const updateVisibility = () => {
    const path = window.location.pathname || '/';
    // Hide on dashboard route; show otherwise
    const onDashboard = path === '/dashboard' || path === '/dashboard/' || path === '/';
    btn.style.display = onDashboard ? 'none' : 'inline-flex';
  };

  updateVisibility();
  window.addEventListener('single-spa:routing-event', updateVisibility);
}
let reactRoot: ReactDOM.Root | null = null;
function mountReactApp(): void {
  const rootElement = document.getElementById('root');
  if (rootElement && !reactRoot) {
    reactRoot = ReactDOM.createRoot(rootElement);
    reactRoot.render(React.createElement(App));
  }
}
async function startMicrofrontends(initialUrl?: string): Promise<void> {
  if (reactRoot) {
    reactRoot.unmount();
    reactRoot = null;
  }
  const response = await fetch('/microfrontend-layout.html');
  const layoutHtml = await response.text();
  const routes = constructRoutes(layoutHtml);
  const applications = constructApplications({
    routes,
    loadApp: ({ name }) => {
      const spec = String(name);
      console.log('[Root-Config] Loading application:', spec);

      // Always use SystemJS to load microfrontends to support System.register bundles
      // Special-case Angular name to bypass registry aliasing issues
      if (spec === 'cms-angular') {
        const angularUrl = 'https://cms.coherent.in/single-spa/main.js';
        console.log('[Root-Config] Loading Angular app from:', angularUrl);
        return (window as any).System.import(angularUrl)
          .then((app: any) => {
            console.log('[Root-Config] ✓ Angular app loaded successfully');
            return app;
          })
          .catch((error: any) => {
            console.error('[Root-Config] ✗ Failed to load Angular app:', error);
            throw error;
          });
      }

      if (spec.startsWith('https://cms.coherent.in') && spec.endsWith('/main.js') && !spec.includes('/single-spa/')) {
        const angularUrl = 'https://cms.coherent.in/single-spa/main.js';
        console.log('[Root-Config] Loading Angular app (redirected) from:', angularUrl);
        return (window as any).System.import(angularUrl);
      }

      if (spec === 'https://help.coherent.in/single-spa/app.js' || spec === 'https://zentra.coherent.in/single-spa/zentra-app.js') {
        console.log('[Root-Config] Loading visitor management app from:', spec);
        return import(/* webpackIgnore: true */ name as string);
      }

      // Default fallback
      console.log('[Root-Config] Loading app via SystemJS:', spec);
      return System.import(spec);
    }
  });
  const layoutEngine = constructLayoutEngine({ routes, applications });
  // Register a dashboard app inside single-spa that mounts the React dashboard
  registerApplication({
    name: 'root-dashboard',
    app: {
      bootstrap: () => Promise.resolve(),
      mount: async () => {
        mountReactApp();
      },
      unmount: async () => {
        if (reactRoot) {
          reactRoot.unmount();
          reactRoot = null;
        }
      }
    },
    activeWhen: (location) => location.pathname === '/dashboard' || location.pathname === '/dashboard/'
  });
  applications.forEach((app) => {
    if (Array.isArray(app.activeWhen)) {
      const isActive = app.activeWhen.some((fn) => {
        if (typeof fn === 'function') {
          try {
            // Pass window.location, not just pathname
            return fn(window.location);
          } catch (e) {
            console.warn('[Root-Config] activeWhen function threw:', e);
            return false;
          }
        }
        return false;
      });
      console.log(`[Root-Config] App "${app.name}" activeWhen result:`, isActive);
    }

    console.log(`[Root-Config] Registering app:`, app.name, 'activeWhen:', app.activeWhen);

    const customProps = {
      accessToken: localStorage.getItem('accessToken') || '',
      __CMS_ASSET_BASE__: 'https://cms.coherent.in/single-spa'
    };

    console.log(`[Root-Config] Custom props for "${app.name}":`, customProps);

    registerApplication({
      name: app.name,
      app: app.app,
      activeWhen: app.activeWhen,
      customProps
    });
  });
  layoutEngine.activate();
  start();
  // Ensure the global back button exists after starting single-spa
  ensureGlobalBackButton();
  if (initialUrl) {
    navigateToUrl(initialUrl);
  }
}
// Expose a global to trigger MFE startup after login/dashboard
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).startMicrofrontends = startMicrofrontends;

function shouldStartMicrofrontend(pathname: string): boolean {
  const normalized = pathname || '/';
  const microPrefixes = ['/chdPortal', '/zentra', '/angular'];

  return microPrefixes.some((prefix) => normalized.startsWith(prefix));
}

const initialPath = window.location.pathname;
const initialUrl = initialPath + (window.location.search || '') + (window.location.hash || '');

if (shouldStartMicrofrontend(initialPath)) {
  startMicrofrontends(initialUrl).catch((error) => {
    console.error('[Root-Config] ✗ Failed to start microfrontends on initial load:', error);
    // Fallback to mounting React SPA so user has a way to recover
    mountReactApp();
  });
} else {
  // Mount the React SPA by default
  mountReactApp();
}
